import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { ChevronLeft, Compass } from 'lucide-react';
import 'maplibre-gl/dist/maplibre-gl.css';
import { loadGlobalTouringRoutes } from './touring/touringRoutesData';
import { attachTouringRoutesMaplibre, buildTouringPopupHTML } from './touring/touringRoutesLayer';

const DEM_TILES = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';

const LABEL_TILES = [
  'https://a.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}.png',
  'https://b.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}.png',
  'https://c.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}.png',
  'https://d.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}.png',
];

const BASEMAP_PRESETS = {
  satellite: {
    label: 'Satellite',
    tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
    attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
    needsLabels: true,
    maxzoom: 19,
  },
  topo: {
    label: 'Topo',
    tiles: [
      'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://c.tile.opentopomap.org/{z}/{x}/{y}.png',
    ],
    attribution: 'Map data © OpenStreetMap contributors, SRTM | Map style © OpenTopoMap (CC-BY-SA)',
    needsLabels: false,
    maxzoom: 17,
  },
  streets: {
    label: 'Streets',
    tiles: [
      'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
      'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
      'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
      'https://d.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
    ],
    attribution: '© OpenStreetMap contributors © CARTO',
    needsLabels: false,
    maxzoom: 19,
  },
};
const DEFAULT_BASEMAP = 'topo';

function buildStyle(basemapKey = DEFAULT_BASEMAP) {
  const preset = BASEMAP_PRESETS[basemapKey] || BASEMAP_PRESETS[DEFAULT_BASEMAP];
  return {
    version: 8,
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    sky: {
      'sky-color': '#a8c7e8',
      'horizon-color': '#e6eef5',
      'fog-color': '#dbe5ee',
      'sky-horizon-blend': 0.6,
      'horizon-fog-blend': 0.5,
      'fog-ground-blend': 0.3,
      'atmosphere-blend': [
        'interpolate', ['linear'], ['zoom'],
        0, 1,
        12, 0.6,
        15, 0.2,
      ],
    },
    sources: {
      basemap: {
        type: 'raster',
        tiles: preset.tiles,
        tileSize: 256,
        maxzoom: preset.maxzoom,
        attribution: preset.attribution,
      },
      labels: {
        type: 'raster',
        tiles: LABEL_TILES,
        tileSize: 256,
        attribution: '© OpenStreetMap contributors © CARTO',
      },
      dem: {
        type: 'raster-dem',
        tiles: [DEM_TILES],
        tileSize: 256,
        encoding: 'terrarium',
        maxzoom: 15,
        attribution: 'Elevation tiles © Mapzen / AWS Open Data',
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': '#dbe5ee' } },
      { id: 'basemap', type: 'raster', source: 'basemap' },
      {
        id: 'hillshade',
        type: 'hillshade',
        source: 'dem',
        paint: {
          'hillshade-exaggeration': 0.35,
          'hillshade-shadow-color': '#1a2634',
          'hillshade-highlight-color': '#ffffff',
        },
      },
      {
        id: 'labels',
        type: 'raster',
        source: 'labels',
        layout: { visibility: preset.needsLabels ? 'visible' : 'none' },
        paint: { 'raster-opacity': 0.9 },
      },
    ],
  };
}

const toResortSlug = (name, id) => {
  const slug = name.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug}-${id.substring(0, 8)}`;
};

const formatSnowfall = (cm) => {
  if (cm === null || cm === undefined || isNaN(cm)) return '0.0 cm';
  return `${Number(cm).toFixed(1)} cm`;
};

// Mirrors createPopupContent in SkiAreasMap.js so the 2D and 3D popups stay
// visually consistent. Kept verbatim instead of factored out — the 2D version
// is constructed from Leaflet, the 3D from MapLibre, and small drift between
// the two is fine. Extract if a third caller appears.
const buildPopupHTML = (properties) => {
  const snowfall24h = properties.recentSnowfall?.snowfall24h ?? properties.snowfall24h;
  const snowfall48h = properties.recentSnowfall?.snowfall48h ?? properties.snowfall48h;
  const snowfall72h = properties.recentSnowfall?.snowfall72h ?? properties.snowfall72h;

  return `
    <div class="card border-0" style="min-width: 220px; max-width: 320px; background: var(--pm-bg-surface); color: var(--pm-text); overflow: hidden; border-radius: 12px;">
      <div style="background: var(--pm-popup-header-bg); padding: 12px 16px; margin: -1px -1px 0 -1px;">
        <h5 class="mb-0" style="color: var(--pm-popup-header-text);">${properties.name || 'Unknown Location'}</h5>
      </div>
      <div class="card-body p-3" style="background: var(--pm-bg-surface);">
        <div class="mb-3">
          <h6 class="mb-2 small fw-bold" style="color: var(--pm-text-muted);">Snow Forecast</h6>
          <div class="row g-2">
            <div class="col-4">
              <div style="background: var(--pm-bg-elevated); border: 1px solid var(--pm-border); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">Next 24h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(properties.next24Hours)}</div>
              </div>
            </div>
            <div class="col-4">
              <div style="background: var(--pm-bg-elevated); border: 1px solid var(--pm-border); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">Next 48h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(properties.next48Hours)}</div>
              </div>
            </div>
            <div class="col-4">
              <div style="background: var(--pm-bg-elevated); border: 1px solid var(--pm-border); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">Next 72h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(properties.next72Hours)}</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h6 class="mb-2 small fw-bold" style="color: var(--pm-text-muted);">Recent Snowfall</h6>
          <div class="row g-2">
            <div class="col-4">
              <div style="background: rgba(0, 212, 255, 0.08); border: 1px solid var(--pm-accent-muted); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">24h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(snowfall24h)}</div>
              </div>
            </div>
            <div class="col-4">
              <div style="background: rgba(0, 212, 255, 0.08); border: 1px solid var(--pm-accent-muted); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">48h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(snowfall48h)}</div>
              </div>
            </div>
            <div class="col-4">
              <div style="background: rgba(0, 212, 255, 0.08); border: 1px solid var(--pm-accent-muted); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">72h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(snowfall72h)}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-3 pt-3" style="border-top: 1px solid var(--pm-border);">
          <button
            class="btn btn-primary btn-sm w-100"
            onclick="window.location.href='/resort/${toResortSlug(properties.name, properties.id)}'"
          >
            View Full Details
          </button>
        </div>
      </div>
    </div>
  `;
};

const RegionalMap3D = ({ resorts, initialBounds, onClose }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [basemapKey, setBasemapKey] = useState(DEFAULT_BASEMAP);
  const basemapKeyRef = useRef(DEFAULT_BASEMAP);
  const [touringVisible, setTouringVisible] = useState(false);
  const touringCtrlRef = useRef(null);
  const touringDataRef = useRef(null);
  const touringLoadingRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;
    let map;

    (async () => {
      const maplibregl = (await import('maplibre-gl')).default;
      if (cancelled || !containerRef.current) return;

      map = new maplibregl.Map({
        container: containerRef.current,
        style: buildStyle(basemapKeyRef.current),
        center: initialBounds
          ? [
              (initialBounds[0][0] + initialBounds[1][0]) / 2,
              (initialBounds[0][1] + initialBounds[1][1]) / 2,
            ]
          : [-110, 40],
        zoom: 8,
        pitch: 60,
        bearing: -20,
        maxPitch: 85,
        hash: false,
        attributionControl: { compact: true },
      });
      mapRef.current = map;

      requestAnimationFrame(() => {
        if (!cancelled && mapRef.current) mapRef.current.resize();
      });

      map.on('error', (e) => {
        // eslint-disable-next-line no-console
        console.warn('[RegionalMap3D]', e && e.error ? e.error.message : e);
      });

      map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right');
      map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left');

      const resizeObserver = new ResizeObserver(() => {
        if (mapRef.current) mapRef.current.resize();
      });
      resizeObserver.observe(containerRef.current);
      map._pmResizeObserver = resizeObserver;

      map.on('load', () => {
        if (cancelled) return;
        try {
          map.resize();
          try {
            map.setTerrain({ source: 'dem', exaggeration: 1.5 });
          } catch (err) {
            console.warn('[RegionalMap3D] setTerrain failed:', err);
          }

          if (initialBounds) {
            map.fitBounds(initialBounds, { pitch: 60, bearing: -20, padding: 60, duration: 0 });
          }

          map.addSource('resorts', {
            type: 'geojson',
            data: resorts || { type: 'FeatureCollection', features: [] },
          });
          map.addLayer({
            id: 'resorts-dot',
            type: 'circle',
            source: 'resorts',
            paint: {
              'circle-radius': 6,
              'circle-color': '#0891b2',
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 1.5,
            },
          });
          map.addLayer({
            id: 'resorts-label',
            type: 'symbol',
            source: 'resorts',
            layout: {
              'text-field': ['get', 'name'],
              'text-font': ['Noto Sans Regular'],
              'text-size': 12,
              'text-offset': [0, -1.1],
              'text-anchor': 'bottom',
            },
            paint: {
              'text-color': '#1e293b',
              'text-halo-color': '#ffffff',
              'text-halo-width': 1.6,
            },
          });

          // Hit-test with a tolerance bbox so small dots / label clicks both work.
          const HIT_TOLERANCE = 12;
          const TOURING_HIT = 8;
          const pickResort = (e) => {
            const p = e.point;
            const bbox = [
              [p.x - HIT_TOLERANCE, p.y - HIT_TOLERANCE],
              [p.x + HIT_TOLERANCE, p.y + HIT_TOLERANCE],
            ];
            const feats = map.queryRenderedFeatures(bbox, {
              layers: ['resorts-dot', 'resorts-label'],
            });
            return feats[0] || null;
          };
          const pickTouring = (e) => {
            const lid = touringCtrlRef.current?.layerIds?.line;
            if (!lid || !map.getLayer(lid)) return null;
            const p = e.point;
            const bbox = [
              [p.x - TOURING_HIT, p.y - TOURING_HIT],
              [p.x + TOURING_HIT, p.y + TOURING_HIT],
            ];
            const feats = map.queryRenderedFeatures(bbox, { layers: [lid] });
            return feats[0] || null;
          };

          map.on('mousemove', (e) => {
            map.getCanvas().style.cursor = (pickResort(e) || pickTouring(e)) ? 'pointer' : '';
          });

          map.on('click', (e) => {
            // Resort dots take priority since they have the bigger hit target.
            const resortFeature = pickResort(e);
            if (resortFeature) {
              new maplibregl.Popup({ closeButton: true, maxWidth: '320px' })
                .setLngLat(resortFeature.geometry.coordinates)
                .setHTML(buildPopupHTML(resortFeature.properties))
                .addTo(map);
              return;
            }
            const touringFeature = pickTouring(e);
            if (touringFeature) {
              new maplibregl.Popup({ closeButton: true, maxWidth: '280px' })
                .setLngLat(e.lngLat)
                .setHTML(buildTouringPopupHTML(touringFeature.properties))
                .addTo(map);
            }
          });
        } catch (err) {
          console.warn('[RegionalMap3D] load handler failed:', err);
        } finally {
          if (!cancelled) setLoading(false);
        }
      });
    })();

    return () => {
      cancelled = true;
      if (touringCtrlRef.current) {
        touringCtrlRef.current.destroy();
        touringCtrlRef.current = null;
      }
      if (mapRef.current) {
        if (mapRef.current._pmResizeObserver) mapRef.current._pmResizeObserver.disconnect();
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // initialBounds is captured once at mount — re-mounting on toggle gives a fresh frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lazy-load + attach the touring routes layer when the user opts in.
  useEffect(() => {
    let cancelled = false;
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) {
      // Style not ready yet — re-run when the map finishes loading. The
      // dependency on `loading` covers this: setLoading(false) runs after the
      // style load handler completes.
      if (!touringVisible) return undefined;
    }

    if (!touringVisible) {
      if (touringCtrlRef.current) touringCtrlRef.current.setVisible(false);
      return undefined;
    }

    if (touringLoadingRef.current) return undefined;
    touringLoadingRef.current = true;
    (async () => {
      if (!touringDataRef.current) {
        touringDataRef.current = await loadGlobalTouringRoutes();
      }
      if (cancelled || !mapRef.current) return;
      if (!touringCtrlRef.current) {
        touringCtrlRef.current = attachTouringRoutesMaplibre(mapRef.current, touringDataRef.current, {
          visible: true,
        });
      } else {
        touringCtrlRef.current.setData(touringDataRef.current);
        touringCtrlRef.current.setVisible(true);
      }
      touringLoadingRef.current = false;
    })();
    return () => { cancelled = true; };
  }, [touringVisible, loading]);

  // Push filtered resorts into the existing source whenever the prop changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const src = map.getSource('resorts');
    if (src) src.setData(resorts || { type: 'FeatureCollection', features: [] });
  }, [resorts]);

  useEffect(() => {
    basemapKeyRef.current = basemapKey;
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const preset = BASEMAP_PRESETS[basemapKey] || BASEMAP_PRESETS[DEFAULT_BASEMAP];
    const src = map.getSource('basemap');
    if (src && typeof src.setTiles === 'function') {
      src.setTiles(preset.tiles);
    }
    if (map.getLayer('labels')) {
      map.setLayoutProperty('labels', 'visibility', preset.needsLabels ? 'visible' : 'none');
    }
  }, [basemapKey]);

  return (
    <div className="regional3d-wrapper">
      <div ref={containerRef} className="regional3d-map" />

      <button
        type="button"
        className="regional3d-back"
        onClick={onClose}
        aria-label="Back to 2D map"
      >
        <ChevronLeft size={16} />
        <span>Back to 2D</span>
      </button>

      <div className="regional3d-basemap-toggle" role="group" aria-label="Basemap">
        {Object.entries(BASEMAP_PRESETS).map(([key, preset]) => (
          <button
            key={key}
            type="button"
            className={`regional3d-basemap-btn${basemapKey === key ? ' active' : ''}`}
            onClick={() => setBasemapKey(key)}
            disabled={loading}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          className={`regional3d-basemap-btn${touringVisible ? ' active' : ''}${touringVisible ? ' terrain3d-touring-btn' : ''}`}
          onClick={() => setTouringVisible((v) => !v)}
          disabled={loading}
          title="Toggle ski-touring routes"
          style={touringVisible ? { background: '#a855f7', borderColor: '#a855f7', color: '#fff' } : undefined}
        >
          <Compass size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          Touring
        </button>
      </div>

      {loading && (
        <div className="regional3d-loading">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading terrain…
        </div>
      )}
    </div>
  );
};

export default RegionalMap3D;
